import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import SEO from "@/components/app/SEO";

export default function PolitiqueConfidentialitePage() {
  const [cfg, setCfg] = useState(null);
  useEffect(() => { api.get("/config/legal").then(({ data }) => setCfg(data)).catch(() => {}); }, []);

  const contactEmail = cfg?.privacy_contact_email || "contact@resiliationexpresse.fr";
  const dpoName = cfg?.data_protection?.name || "Leger Haubert";
  const dpoRole = cfg?.data_protection?.role || "Référent interne à la protection des données";

  return (
    <div className="mx-auto max-w-3xl px-4 py-16 space-y-8">
      <SEO title="Politique de confidentialité" description="Comment Résiliation Express collecte, utilise et protège vos données personnelles conformément au RGPD." canonical="/politique-confidentialite" />
      <header>
        <h1 className="text-4xl font-bold tracking-tight mb-2">Politique de confidentialité</h1>
        <p className="text-sm text-muted-foreground">Dernière mise à jour : {new Date().toLocaleDateString("fr-FR", { year: "numeric", month: "long", day: "numeric" })} · Version 1.0</p>
      </header>

      <section>
        <h2 className="text-xl font-semibold mb-3">1. Responsable du traitement</h2>
        <div className="text-sm space-y-1">
          <p><strong>Résiliation Express</strong>, SASU au capital de 100 €</p>
          <p>Siège : 60 rue François 1er, 75008 Paris — SIREN 107 406 878, RCS Paris</p>
          <p>Représentée par Leger Haubert, Président.</p>
        </div>
      </section>

      <section>
        <h2 className="text-xl font-semibold mb-3">2. Référent à la protection des données</h2>
        <p className="text-sm leading-relaxed">
          <strong>{dpoName}</strong> — {dpoRole}. Pour toute question relative à la protection de vos données ou pour exercer vos droits, vous pouvez contacter le référent à la protection des données de Résiliation Express à l'adresse : <a href={`mailto:${contactEmail}`} className="text-primary">{contactEmail}</a>.
        </p>
        <p className="text-xs text-muted-foreground mt-2">Statut de la désignation auprès de la CNIL : {cfg?.data_protection?.cnil_status || "non déclaré comme DPO officiel"}. Résiliation Express prévoit une évaluation ultérieure de la désignation d'un DPO externe indépendant si les conditions posées par le RGPD l'exigent.</p>
      </section>

      <section>
        <h2 className="text-xl font-semibold mb-3">3. Données collectées</h2>
        <ul className="text-sm list-disc list-inside space-y-1">
          <li>Identification : nom, prénom, adresse postale, email, téléphone (facultatif)</li>
          <li>Compte : identifiants de connexion, mot de passe (haché), historique de connexions</li>
          <li>Dossier de résiliation : nature du contrat, numéro de client/contrat, motif, justificatifs éventuels</li>
          <li>Paiement : identifiants de transaction PayPal (aucun numéro de carte bancaire n'est stocké sur nos serveurs)</li>
          <li>Postal : adresses de destinataire et d'expéditeur, numéro de suivi, preuves de dépôt et de distribution</li>
          <li>Technique : adresse IP tronquée, journaux d'accès, préférences de cookies, données d'usage pseudonymisées</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold mb-3">4. Finalités et bases légales</h2>
        <div className="rounded-lg border border-border overflow-hidden text-sm">
          <table className="w-full">
            <thead className="bg-secondary/50">
              <tr>
                <th className="text-left px-3 py-2">Finalité</th>
                <th className="text-left px-3 py-2">Base légale</th>
                <th className="text-left px-3 py-2">Durée</th>
              </tr>
            </thead>
            <tbody>
              {[
                ["Création et gestion du compte", "Exécution du contrat", "Durée de la relation contractuelle"],
                ["Traitement de la demande de résiliation", "Exécution du contrat", "Durée du dossier + 10 ans (preuve)"],
                ["Génération et envoi des courriers", "Exécution du contrat", "10 ans (preuve légale)"],
                ["Gestion des paiements et facturation", "Obligation légale (comptabilité)", "10 ans"],
                ["Suivi postal", "Exécution du contrat", "Durée du dossier + preuves 10 ans"],
                ["Relation et assistance client", "Intérêt légitime", "3 ans après dernier contact"],
                ["Prévention de la fraude et sécurité", "Intérêt légitime", "13 mois maximum"],
                ["Mesure d'audience anonymisée", "Consentement", "13 mois maximum"],
                ["Prospection commerciale", "Consentement / intérêt légitime", "3 ans après dernier contact"],
              ].map((row) => (
                <tr key={row[0]} className="border-t border-border">
                  {row.map((c, i) => <td key={i} className="px-3 py-2">{c}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-muted-foreground mt-3">Les durées ci-dessus sont susceptibles d'évoluer après validation juridique. Toute modification donnera lieu à une mise à jour datée de cette politique.</p>
      </section>

      <section>
        <h2 className="text-xl font-semibold mb-3">5. Destinataires et sous-traitants</h2>
        <p className="text-sm leading-relaxed">Vos données sont accessibles uniquement au personnel habilité de Résiliation Express. Nous faisons appel aux sous-traitants suivants, encadrés par des contrats conformes à l'article 28 du RGPD :</p>
        <ul className="text-sm list-disc list-inside mt-2 space-y-1">
          <li><strong>PayPal Europe (S.à r.l. et Cie, S.C.A.)</strong> — traitement des paiements</li>
          <li><strong>Prestataire postal</strong> — envoi en recommandé (identité communiquée sur demande)</li>
          <li><strong>Hébergeur cloud (Union européenne)</strong> — infrastructure d'hébergement</li>
          <li><strong>Google Ireland Limited</strong> — mesure d'audience (Google Analytics 4), uniquement avec votre consentement</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold mb-3">6. Transferts hors Union européenne</h2>
        <p className="text-sm leading-relaxed">L'hébergement principal de vos données est localisé dans l'Union européenne. Certains sous-traitants (PayPal, Google) peuvent transférer des données en dehors de l'UE. Ces transferts sont encadrés par des Clauses Contractuelles Types adoptées par la Commission européenne ou d'autres garanties appropriées prévues aux articles 46 et suivants du RGPD.</p>
      </section>

      <section>
        <h2 className="text-xl font-semibold mb-3">7. Sécurité</h2>
        <p className="text-sm leading-relaxed">Chiffrement TLS pour les échanges, chiffrement au repos pour les données sensibles, contrôle d'accès par rôle, journaux d'accès horodatés, tests de sécurité réguliers, protection contre les injections et les téléversements dangereux. Les secrets techniques (clés PayPal, tokens) ne sont jamais stockés dans le navigateur, le dépôt de code, ou les journaux.</p>
      </section>

      <section>
        <h2 className="text-xl font-semibold mb-3">8. Vos droits</h2>
        <p className="text-sm leading-relaxed">Vous disposez des droits d'accès, de rectification, d'effacement, d'opposition, de limitation, de portabilité et du droit de retirer votre consentement à tout moment. Vous pouvez exercer ces droits en écrivant à <a href={`mailto:${contactEmail}`} className="text-primary">{contactEmail}</a>. Nous répondons sous 30 jours au maximum.</p>
        <p className="text-sm leading-relaxed mt-3">Vous disposez également du droit d'introduire une réclamation auprès de la <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-primary">CNIL</a> (Commission Nationale de l'Informatique et des Libertés).</p>
      </section>

      <section>
        <h2 className="text-xl font-semibold mb-3">9. Données des mineurs</h2>
        <p className="text-sm leading-relaxed">Le service n'est pas destiné aux mineurs. Nous ne collectons pas sciemment de données concernant des personnes de moins de 15 ans. Toute donnée détectée comme appartenant à un mineur sera supprimée à réception de l'information.</p>
      </section>

      <section>
        <h2 className="text-xl font-semibold mb-3">10. Décisions automatisées</h2>
        <p className="text-sm leading-relaxed">Résiliation Express ne prend aucune décision produisant des effets juridiques à votre égard sur le seul fondement d'un traitement automatisé, y compris le profilage.</p>
      </section>

      <section>
        <h2 className="text-xl font-semibold mb-3">11. Cookies</h2>
        <p className="text-sm leading-relaxed">Voir notre <a href="/cookies" className="text-primary">politique de cookies</a>. Aucun cookie non essentiel n'est déposé sans votre consentement explicite.</p>
      </section>

      <section>
        <h2 className="text-xl font-semibold mb-3">12. Modifications</h2>
        <p className="text-sm leading-relaxed">Cette politique peut évoluer. Toute modification substantielle est signalée sur le site et communiquée aux utilisateurs. La date de mise à jour figure en tête de page. Les versions précédentes sont conservées en interne.</p>
      </section>
    </div>
  );
}
